import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProductOrdersTimeline } from "../../../api/productApi";
import BookHistoryTimeline from "../../common/BookHistoryTimeline";

/* ✅ cache by: productId-page */
const historyCache = new Map();

export default function BookHistory({ book, onClose, onViewChange }) {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const navigate = useNavigate();

    const handleMemberClick = (memberId) => {
        navigate("/members", { state: { memberId } });
    };

    const handleOrderClick = (orderId) => {
        navigate(`/orders?id=${orderId}`);
    };

    const handleAddDamage = (item, initialData) => {
        if (onViewChange) {
            onViewChange("BOOK_DAMAGE", {
                memberId: initialData.memberId,
                orderId: initialData.orderId,
            });
        }
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [timeline, setTimeline] = useState([]);
    const [product, setProduct] = useState(null);

    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);

    useEffect(() => {
        if (book?.id) setPage(1);
    }, [book?.id]);

    const cacheKey = useMemo(() => {
        if (!book?.id) return "";
        return `${book.id}-${page}`;
    }, [book?.id, page]);

    useEffect(() => {
        if (!book?.id) return;

        let ignore = false;

        // ✅ show cached instantly
        if (historyCache.has(cacheKey)) {
            const cached = historyCache.get(cacheKey);
            setProduct(cached.product);
            setTimeline(cached.timeline);
            setPagination(cached.pagination);
            setLoading(false);
        } else {
            setLoading(true);
        }

        async function loadHistory() {
            try {
                setError("");

                const res = await getProductOrdersTimeline({
                    productId: book.id,
                    token,
                    page,
                    perPage: 5,
                });

                if (ignore) return;

                if (!res?.success) {
                    setError("Book history not available");
                    return;
                }

                const dataToStore = {
                    product: res.product || null,
                    timeline: Array.isArray(res.data) ? res.data : [],
                    pagination: res.pagination || null,
                };

                historyCache.set(cacheKey, dataToStore);

                setProduct(dataToStore.product);
                setTimeline(dataToStore.timeline);
                setPagination(dataToStore.pagination);
            } catch (e) {
                if (!ignore) setError("Failed to load book history");
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        // ✅ background refresh even if cached
        loadHistory();

        return () => {
            ignore = true;
        };
    }, [book?.id, token, page, cacheKey]);

    if (!book) {
        return (
            <div className="text-center text-gray-400 py-10">No Book Selected</div>
        );
    }

    // block only if nothing is cached yet
    if (loading && timeline.length === 0) {
        return (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
                Loading book history…
            </div>
        );
    }

    if (error && timeline.length === 0) {
        return (
            <div className="text-center space-y-4">
                <p className="text-red-500">{error}</p>
                <button
                    onClick={onClose}
                    className="bg-black text-white px-4 py-2 rounded"
                >
                    Close
                </button>
            </div>
        );
    }

    const headerImg =
        product?.image || book.img || "https://via.placeholder.com/120x180";

    const headerName = product?.name || book.title || "-";

    const headerCredits = product?.price
        ? `${product.price} Credits`
        : `${book.credits || "-"} Credits`;

    const stock = product?.stock_status || book.stock_status || "Available";
    const isOut = stock === "outofstock";

    return (
        <div className="w-full">
            {/* TITLE */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold">Book History</h2>
                {loading && <span className="text-xs text-gray-400">Refreshing…</span>}
            </div>

            {/* TOP BOOK STRIP */}
            <div className="border-b pb-6 mb-6 flex items-start gap-4">
                <div className="w-20 h-24 flex-shrink-0 overflow-hidden rounded-md border">
                    <img
                        src={headerImg}
                        alt={headerName}
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="space-y-1">
                    <h3 className="text-lg font-semibold">{headerName}</h3>

                    <p className="text-sm text-blue-600 font-medium">{headerCredits}</p>

                    <p className="text-sm text-gray-500">
                        {isOut ? "Out of stock" : "Available"}
                    </p>

                    <p className="text-sm text-gray-400">{book.id}</p>
                </div>
            </div>

            {/* LIST */}
            <div className="mt-4">
                <BookHistoryTimeline
                    timeline={timeline}
                    loading={loading}
                    token={token}
                    onMemberClick={handleMemberClick}
                    onOrderClick={handleOrderClick}
                    onAddDamage={handleAddDamage}
                />
            </div>

            {/* PAGINATION */}
            {pagination?.total_pages > 1 && (
                <div className="flex items-center justify-between mt-6">
                    <button
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className={`px-4 py-2 rounded border text-sm ${page <= 1
                            ? "text-gray-300 border-gray-200 cursor-not-allowed"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                    >
                        Prev
                    </button>

                    <p className="text-sm text-gray-500">
                        Page <span className="font-semibold">{page}</span> of{" "}
                        <span className="font-semibold">{pagination.total_pages}</span>
                    </p>

                    <button
                        disabled={page >= pagination.total_pages}
                        onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                        className={`px-4 py-2 rounded border text-sm ${page >= pagination.total_pages
                            ? "text-gray-300 border-gray-200 cursor-not-allowed"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* FOOTER BUTTON */}
            <div className="mt-10 flex justify-end">
                <button
                    onClick={onClose}
                    className="bg-black text-white px-10 py-3 rounded-md hover:bg-gray-900 transition"
                >
                    Close
                </button>
            </div>
        </div>
    );
}
